import { useEffect, useRef } from 'react';

/**
 * Custom hook to handle browser back/forward navigation within React app
 * @param {string} currentState - Current state identifier (e.g., 'home', 'loans', 'loan-detail-123')
 * @param {function} onStateChange - Callback when state changes via browser navigation
 * @param {boolean} enabled - Whether to enable browser history tracking
 */
export const useBrowserHistory = (currentState, onStateChange, enabled = true) => {
    const isInitialMount = useRef(true);
    const previousState = useRef(currentState);

    useEffect(() => {
        if (!enabled || !currentState) return;

        // Skip adding history entry on initial mount
        if (isInitialMount.current) {
            isInitialMount.current = false;
            previousState.current = currentState;
            return;
        }

        // Only add history entry if state actually changed
        if (previousState.current !== currentState) {
            // Add new history entry
            window.history.pushState(
                { reactState: currentState },
                '',
                window.location.href
            );
            previousState.current = currentState;
        }
    }, [currentState, enabled]);

    useEffect(() => {
        if (!enabled) return;

        const handlePopState = (event) => {
            // If there's a state in the history, use it
            if (event.state && event.state.reactState) {
                onStateChange(event.state.reactState);
            }
            // If no state, don't call onStateChange - let the parent component handle it
            // This prevents unwanted navigation to 'home' when going back from detail views
        };

        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [onStateChange, enabled]);
};

